/*
 * Product reviews for the PDP.
 *
 * Judge.me's product widget brings its own histogram, buttons and colors.
 * This asks their public reviews endpoint for the current product and draws
 * only the review text, in the same style as the reviews drawer.
 */
class ProductReviews extends HTMLElement {
  connectedCallback() {
    this.shop = this.dataset.shopDomain;
    this.productId = this.dataset.productId;
    this.limit = Number(this.dataset.limit) || 6;
    this.list = this.querySelector('[data-product-reviews-list]');
    this.summary = this.querySelector('[data-product-reviews-summary]');
    this.empty = this.querySelector('[data-product-reviews-empty]');
    this.more = this.querySelector('[data-product-reviews-more]');
    this.reviews = [];
    this.shown = 0;

    this.more?.addEventListener('click', () => this.showMore());
    this.bindRatingLinks();
    this.load();
  }

  bindRatingLinks() {
    if (!this.id) this.id = 'product-reviews';

    document.querySelectorAll('[data-product-judge-rating]').forEach((badge) => {
      badge.addEventListener('click', (event) => {
        event.preventDefault();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  syncRatingBadge(count, average) {
    document.querySelectorAll('[data-product-judge-rating]').forEach((badge) => {
      if (!count) {
        badge.hidden = true;
        return;
      }

      const rounded = Math.round(Number(average) * 10) / 10;
      const score = String(rounded).replace('.', ',');
      const label = count === 1 ? 'recenzie' : 'recenzii';
      const stars = badge.querySelector('[data-judge-stars]');
      const text = badge.querySelector('[data-judge-score]');

      badge.hidden = false;
      if (stars) {
        stars.innerHTML = this.starMarkup(average);
        stars.setAttribute('aria-label', `${score} din 5`);
      }
      if (text) text.textContent = `${score} · ${count} ${label}`;
    });
  }

  async load() {
    if (!this.shop || !this.productId) return;

    try {
      let page = 1;
      let totalPages = 1;
      let summary = null;
      const reviews = [];

      while (page <= totalPages && page <= 10) {
        const data = await this.fetchPage(page);
        if (!summary) summary = data;
        reviews.push(...(data.reviews || []));
        totalPages = data.pagination?.total_pages || 1;
        page += 1;
      }

      this.reviews = reviews;
      if (!reviews.length) {
        this.syncRatingBadge(0, 0);
        this.showEmpty();
        return;
      }

      this.hidden = false;
      this.renderSummary(summary);
      this.shown = 0;
      this.showMore();
    } catch (error) {
      this.showEmpty();
    }
  }

  async fetchPage(page) {
    const url = new URL('https://cdn.judge.me/reviews/reviews_for_widget');
    url.searchParams.set('url', this.shop);
    url.searchParams.set('shop_domain', this.shop);
    url.searchParams.set('platform', 'shopify');
    url.searchParams.set('product_id', this.productId);
    url.searchParams.set('page', String(page));
    url.searchParams.set('per_page', '10');

    const response = await fetch(url.href);
    if (!response.ok) throw new Error('reviews');
    return response.json();
  }

  showEmpty() {
    if (this.hasAttribute('data-hide-empty')) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    if (this.empty) this.empty.hidden = false;
  }

  renderSummary(data) {
    if (!this.summary || !data) return;

    const count = Number(data.number_of_reviews) || this.reviews.length;
    const average = Number(data.average_rating) || 0;
    const rounded = Math.round(average * 10) / 10;
    const score = String(rounded).replace('.', ',');
    const label = count === 1 ? 'recenzie' : 'recenzii';

    this.syncRatingBadge(count, average);
    this.summary.hidden = false;
    this.summary.innerHTML = `
      <span class="product-reviews__stars" aria-label="${score} din 5">${this.starMarkup(average)}</span>
      <span class="product-reviews__score">${score} din 5 · ${count} ${label}</span>
    `;
  }

  showMore() {
    const next = this.reviews.slice(this.shown, this.shown + this.limit);
    next.forEach((review) => this.list.append(this.reviewItem(review)));
    this.shown += next.length;

    if (this.more) this.more.hidden = this.shown >= this.reviews.length;
  }

  reviewItem(review) {
    const item = document.createElement('li');
    item.className = 'product-reviews__review';

    const top = document.createElement('div');
    top.className = 'product-reviews__review-top';

    const stars = document.createElement('span');
    stars.className = 'product-reviews__stars';
    stars.setAttribute('aria-label', `${review.rating || 0} din 5`);
    stars.innerHTML = this.starMarkup(review.rating || 0);

    const time = document.createElement('time');
    time.className = 'product-reviews__date';
    time.dateTime = review.created_at || '';
    time.textContent = this.formatDate(review.created_at);

    top.append(stars, time);
    item.append(top);

    if (review.title) {
      const title = document.createElement('p');
      title.className = 'product-reviews__title';
      title.textContent = review.title;
      item.append(title);
    }

    const text = this.reviewText(review.body_html);
    if (text) {
      const copy = document.createElement('p');
      copy.className = 'product-reviews__text';
      copy.textContent = text;
      item.append(copy);
    }

    if (review.reviewer_name) {
      const author = document.createElement('p');
      author.className = 'product-reviews__author';
      author.textContent = review.reviewer_name;
      item.append(author);
    }

    return item;
  }

  reviewText(html) {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const paragraphs = [...doc.querySelectorAll('p')]
      .map((paragraph) => paragraph.textContent.trim())
      .filter(Boolean);
    if (paragraphs.length) return paragraphs.join('\n\n');
    return doc.body.textContent.trim();
  }

  starMarkup(score) {
    const filled = Math.max(0, Math.min(5, Math.round(Number(score) || 0)));
    return Array.from({ length: 5 }, (_, index) => {
      const state = index < filled ? 'is-on' : 'is-off';
      return `<span class="${state}" aria-hidden="true">★</span>`;
    }).join('');
  }

  formatDate(raw) {
    if (!raw) return '';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }
}

customElements.define('product-reviews', ProductReviews);
